/**
 * The expanded row — list engine v2, "never leave the list to learn one more
 * thing".
 *
 * One component for every module. A tray is panels of lines, and which panels
 * is the area's business, not this file's: Jobs sends Quote, Visits, Notes
 * and Photos; Invoices sends Lines, Payments and Delivery log. Five bespoke
 * trays would be five components that drift apart on padding, on what an empty
 * panel says and on what a slow one looks like — and the tray is the one place
 * in the product where somebody is already mid-thought.
 *
 * It fetches when it opens, because a tray nobody opened is fifty queries
 * nobody asked for.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TrayLine, TrayPanel, TrayPayload } from '@rowted/shared'
import { SectionLabel } from './primitives.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import type { ListArea } from '../lib/use-list.js'

/** The two most likely actions, drawn under the panels. */
export interface TrayAction {
  key: string
  label: string
  /** The record's next-action verb leads, in accent. */
  primary?: boolean
  onSelect: () => void
}

export interface RowTrayProps {
  area: ListArea
  id: string
  actions?: TrayAction[]
  /**
   * How to ask — the same injection `RelatedList` and the card strip take.
   *
   * Rowted Admin's lists live under `/api/v1/platform/…` and carry their own
   * CSRF header, so a tray on one of them passes `adminRequest`. Without it
   * this component reached the tenant base and answered 401, which is why the
   * admin activity list had no chevron for a while: not a missing feature, a
   * fetch that could not have worked.
   */
  request?: typeof request
}

export function RowTray({ area, id, actions, request: send = request }: RowTrayProps) {
  const [payload, setPayload] = useState<TrayPayload | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setPayload(null)
    setError(null)

    send<TrayPayload>(`/${area}/${id}/tray`, { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) setPayload(result) })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : 'That did not load.')
      })

    return () => controller.abort()
  }, [area, id, send])

  // No padding of its own: the row that opens it already supplies the gutter.
  return (
    <div data-tray={id} className="flex flex-col gap-3">
      {error
        ? <p className="m-0 text-cell text-danger">{error}</p>
        : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {payload
              ? payload.panels.map((panel) => <Panel key={panel.label} panel={panel} />)
              // Skeletons rather than a spinner, so the tray does not jump to
              // its real height once the answer arrives (v2's loading rule).
              : [0, 1, 2, 3].map((slot) => <PanelSkeleton key={slot} />)}
          </div>
        )}

      {actions && actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              onClick={action.onSelect}
              className={cn(
                'h-8 rounded-button border px-3 text-cell font-semibold',
                action.primary
                  ? 'border-accent bg-accent text-white hover:bg-accent-dark'
                  : 'border-line bg-surface text-text hover:bg-hover',
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Panel({ panel }: { panel: TrayPanel }) {
  const navigate = useNavigate()

  return (
    <div className="rounded-card border border-line bg-surface p-3">
      <div className="flex items-baseline justify-between gap-2">
        <SectionLabel>{panel.label}</SectionLabel>
        {panel.value && (
          <span className="tnum text-rowsub font-semibold text-text-muted">{panel.value}</span>
        )}
      </div>

      {panel.lines.length === 0
        ? <p className="m-0 mt-1.5 text-rowsub text-text-faint">{panel.empty}</p>
        : (
          <ul className="m-0 mt-1.5 flex list-none flex-col gap-1.5 p-0">
            {panel.lines.map((line, index) => (
              <li key={index}>
                <Line line={line} onOpen={line.href ? () => navigate(line.href!) : undefined} />
              </li>
            ))}
          </ul>
        )}
    </div>
  )
}

const TONE: Record<TrayLine['tone'], string> = {
  plain: 'text-text',
  ok: 'text-accent-dark',
  warn: 'text-warn',
  danger: 'text-danger',
}

function Line({ line, onOpen }: { line: TrayLine; onOpen?: () => void }) {
  const body = (
    <>
      <span className="flex items-baseline justify-between gap-2">
        <span className={cn('min-w-0 truncate text-row font-semibold', TONE[line.tone])}>
          {line.text}
        </span>
        {line.value && (
          <span className="tnum shrink-0 text-rowsub text-text-body">{line.value}</span>
        )}
      </span>
      {line.sub && (
        <span className="block truncate text-rowsub text-text-muted">{line.sub}</span>
      )}
    </>
  )

  if (!onOpen) return <span className="block">{body}</span>

  return (
    <button
      type="button"
      onClick={onOpen}
      className="block w-full rounded-field text-left hover:bg-hover"
    >
      {body}
    </button>
  )
}

function PanelSkeleton() {
  return (
    <div aria-hidden className="rounded-card border border-line bg-surface p-3">
      <span className="block h-2 w-16 rounded-tag bg-line-soft" />
      <span className="mt-2.5 block h-2.5 w-full rounded-tag bg-line-soft" />
      <span className="mt-1.5 block h-2.5 w-2/3 rounded-tag bg-line-soft" />
    </div>
  )
}
